import { Component, computed, effect, inject, QueryList, signal, ViewChildren } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl } from '@angular/forms';
import { NbToastrService } from '@nebular/theme';
import {
  CustomFieldCollectionDetailDto,
  CustomFieldCollectionInstanceMergeDto,
  ProtocolTypeItemDto,
  QrAnonymousMaintenanceTimerFinishDto,
  QrCodeService,
  QrMaintenanceTimerDto,
} from '@artemis-software/wr-api';
import { asyncComputed } from '@/utils/signals/asyncComputed';
import { ContractorCredentialsService } from '@services/contractor-credentials.service';
import { qrBuildingContext } from '@services/qr-building-context.service';
import { CustomFormComponent } from '@shared/custom-form/custom-form/custom-form.component';
import { validateCustomForms } from '@shared/custom-form/custom-form-submit';
import { Attachment, attachmentsFromDetails, attachmentToMerge } from '@shared/form-controls/attachment-upload/attachment-utils';
import { firstValueFrom } from 'rxjs';

export interface FinishSection {
  collection: CustomFieldCollectionDetailDto;
}

// Steps: 0 = info, 1..N = protocol sections
@Component({
  standalone: false,
  selector: 'wr-qr-code-maintenance-timer-finish-page',
  templateUrl: './qr-code-maintenance-timer-finish-page.component.html',
  styleUrls: ['./qr-code-maintenance-timer-finish-page.component.scss'],
})
export class QrCodeMaintenanceTimerFinishPageComponent {

  @ViewChildren(CustomFormComponent) formComponents!: QueryList<CustomFormComponent>;

  private readonly qrCodeService = inject(QrCodeService);
  private readonly contractorCredentialsService = inject(ContractorCredentialsService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly toastr = inject(NbToastrService);

  readonly name = signal('');
  readonly comment = signal('');
  readonly sendEmailToPropertyManager = signal(false);
  readonly attachmentsControl = new FormControl<Attachment[]>(attachmentsFromDetails([]));
  readonly selectedProtocolType = signal<ProtocolTypeItemDto | undefined>(undefined);
  readonly submitting = signal(false);
  readonly currentSectionIndex = signal(0);
  private readonly _sections = signal<FinishSection[]>([]);

  readonly context = qrBuildingContext();
  readonly timerId = this.route.snapshot.paramMap.get('timerId');

  readonly timer = asyncComputed<QrMaintenanceTimerDto | undefined>(() => {
    const id = this.timerId;
    if (!id) return undefined;
    return this.qrCodeService.findMaintenanceTimerById(id);
  }, undefined);

  readonly preset = asyncComputed(() => {
    const type = this.selectedProtocolType();
    if (!type) return undefined;
    return this.qrCodeService.getProtocolPreset(type.id);
  }, undefined);

  readonly hasProtocolTypes = computed(() => {
    const timer = this.timer();
    return !this.context.isExternal() && (timer?.maintenanceProtocolTypes?.length ?? 0) > 0;
  });

  readonly sections = this._sections.asReadonly();

  readonly totalSections = computed(() => this._sections().length);

  readonly isInfoStep = computed(() =>
    !this.selectedProtocolType() || this.totalSections() === 0
  );

  readonly currentSection = computed(() => {
    const sections = this._sections();
    const idx = this.currentSectionIndex();
    return sections[idx] ?? null;
  });

  readonly isLastSection = computed(() =>
    this.currentSectionIndex() >= this.totalSections() - 1
  );

  readonly isFirstSection = computed(() => this.currentSectionIndex() === 0);

  constructor() {
    effect(() => {
      const preset = this.preset();
      if (!preset) {
        this._sections.set([]);
        this.currentSectionIndex.set(0);
        return;
      }
      const collections = preset.collections ?? [];
      this._sections.set(collections.map(c => ({ collection: c })));
      this.currentSectionIndex.set(0);
    });
  }

  daysLeft(): number {
    const t = this.timer();
    if (!t?.nextDate) return 0;
    const diffTime = new Date(t.nextDate).getTime() - new Date().getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  selectProtocolType(type: ProtocolTypeItemDto): void {
    const current = this.selectedProtocolType();
    if (current?.id === type.id) {
      this.selectedProtocolType.set(undefined);
    } else {
      this.selectedProtocolType.set(type);
    }
  }

  nextSection(): void {
    const idx = this.currentSectionIndex();
    if (idx < this.totalSections() - 1) {
      this.currentSectionIndex.set(idx + 1);
    }
  }

  prevSection(): void {
    const idx = this.currentSectionIndex();
    if (idx > 0) {
      this.currentSectionIndex.set(idx - 1);
    }
  }

  goBack(): void {
    this.router.navigate(['../../'], { relativeTo: this.route });
  }

  async submit(): Promise<void> {
    const timer = this.timer();
    if (!timer) return;

    const external = this.context.isExternal();
    const name = this.name().trim();
    if (!external && !name) {
      this.toastr.warning('Bitte geben Sie einen Namen an.', 'Name erforderlich');
      return;
    }

    const currentAttachments: Attachment[] = this.attachmentsControl.value ?? [];
    const activeAttachments = currentAttachments.filter(a => !a.deleted);

    if (timer.imagesMandatory && activeAttachments.length === 0) {
      this.toastr.warning('Bitte laden Sie mindestens ein Bild hoch.', 'Bilder erforderlich');
      return;
    }

    const finisherTenantPin = this.contractorCredentialsService.getCurrentPin();
    if (!finisherTenantPin) return;

    const selectedType = this.selectedProtocolType();
    let protocolInstances: CustomFieldCollectionInstanceMergeDto[] | undefined;

    if (selectedType && this.preset()) {
      const validation = validateCustomForms(this.formComponents);
      if (validation.status === 'not-ready') {
        this.toastr.danger('Formular ist noch nicht bereit.', 'Fehler');
        return;
      }
      if (validation.status === 'invalid') {
        this.toastr.danger(validation.message, validation.title);
        return;
      }
      protocolInstances = this.formComponents.toArray().map(form => form.getMergeDto());
    }

    const dto: QrAnonymousMaintenanceTimerFinishDto = {
      maintenanceTimerId: timer.id,
      finisherName: external ? '' : name,
      finisherTenantPin,
      comment: this.comment(),
      attachments: activeAttachments.map(a => attachmentToMerge(a)),
      protocolTypeId: external ? undefined : selectedType?.id,
      protocolInstances: external ? undefined : protocolInstances,
      sendEmailToPropertyManager: !external && this.sendEmailToPropertyManager(),
    };

    this.submitting.set(true);
    try {
      await firstValueFrom(this.qrCodeService.finishMaintenanceTimer(dto));
      this.toastr.success('Wartungstimer wurde erfolgreich abgeschlossen.', 'Erfolg');
      this.goBack();
    } catch {
      this.toastr.danger('Fehler beim Abschluss des Wartungstimers.', 'Fehler');
    } finally {
      this.submitting.set(false);
    }
  }
}
